import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const SecurityStep = ({ formData, errors, onChange }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (password?.length >= 12) strength += 25;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength += 25;
    if (/\d/?.test(password)) strength += 12.5;
    if (/[^a-zA-Z0-9]/?.test(password)) strength += 12.5;
    return strength;
  };

  const handlePasswordChange = (e) => {
    const password = e?.target?.value;
    setPasswordStrength(calculatePasswordStrength(password));
    onChange(e);
  };

  const getStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
          Secure Your Account
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Create a strong password to protect your intimate information.
        </p>
      </div>
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 md:p-5">
        <div className="flex items-start space-x-3">
          <Icon name="ShieldCheck" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-medium text-foreground mb-2">
              Password Requirements
            </h3>
            <ul className="space-y-1 text-xs md:text-sm text-muted-foreground">
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={14} color="var(--color-success)" />
                <span>At least 8 characters long</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={14} color="var(--color-success)" />
                <span>Mix of uppercase and lowercase letters</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={14} color="var(--color-success)" />
                <span>At least one number</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={14} color="var(--color-success)" />
                <span>At least one special character</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={handlePasswordChange}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-therapeutic"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} color="currentColor" />
          </button>
        </div>

        {formData?.password && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs md:text-sm">
              <span className="text-muted-foreground">Password Strength:</span>
              <span className={`font-medium ${passwordStrength >= 75 ? 'text-success' : passwordStrength >= 50 ? 'text-accent' : 'text-warning'}`}>
                {getStrengthLabel()}
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full transition-therapeutic ${getStrengthColor()}`}
                style={{ width: `${passwordStrength}%` }}
              />
            </div>
          </div>
        )}

        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            placeholder="Re-enter your password"
            value={formData?.confirmPassword}
            onChange={onChange}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-therapeutic"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} color="currentColor" />
          </button>
        </div>
      </div>
      <Input
        label="Security Question"
        type="text"
        name="securityQuestion"
        placeholder="What was the name of your first pet?"
        description="This helps us verify your identity if you forget your password"
        value={formData?.securityQuestion}
        onChange={onChange}
        error={errors?.securityQuestion}
        required
      />
      <Input
        label="Security Answer"
        type="text"
        name="securityAnswer"
        placeholder="Enter your answer"
        value={formData?.securityAnswer}
        onChange={onChange}
        error={errors?.securityAnswer}
        required
      />
    </div>
  );
};

export default SecurityStep;